import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {Box, IconButton, Stack} from '@mui/material';
import CancelIcon from '@mui/icons-material/Close';
import {DateTimePicker, LocalizationProvider} from '@mui/x-date-pickers';
import {AdapterMoment} from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import 'moment/locale/en-gb';
import {toFormExpirationIso, toFormExpirationMoment} from '../../../utility/dateUtil';

// Loading the British setting makes it the default for every date in the app, so the default goes straight back to
// English and only this picker uses the British one.
moment.locale('en');

const FormExpirationDatePicker = ({
    expirationDate,
    onExpirationDateChange,
    disabled = false,
    minDateTime,
    label = 'Expiry date (optional)',
    showClearButton = true,
    hideEmptyPrompt = false,
    sx,
}) => {
    // Walkthrough finding 14: the date reads the British way and the week starts on Monday, for this picker only.
    // Clicking anywhere in the box opens the calendar, as typing into it is not possible.
    const [open, setOpen] = useState(false);
    const value = toFormExpirationMoment(expirationDate);
    const helperText = expirationDate
        // The line under the box reads the same way as the box.
        ? `Expires at ${value.clone().locale('en-gb').format('D MMM YYYY, HH:mm')}`
        : (hideEmptyPrompt ? '' : 'Set an expiration date and time (your local time)');

    return (
        <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{flexGrow: 1, ...sx}}>
                <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="en-gb">
                    <DateTimePicker
                        label={label}
                        disabled={disabled}
                        value={value}
                        open={open}
                        onOpen={() => setOpen(true)}
                        onClose={() => setOpen(false)}
                        onChange={(v) => onExpirationDateChange(toFormExpirationIso(v))}
                        format="D MMM YYYY, HH:mm"
                        minDateTime={minDateTime ?? moment()}
                        ampm={false}
                        slotProps={{
                            textField: {
                                fullWidth: true,
                                helperText,
                                inputProps: {readOnly: true},
                                onClick: () => !disabled && setOpen(true),
                                sx: {'& input': {cursor: disabled ? 'default' : 'pointer'}},
                            },
                            field: {readOnly: true},
                        }}
                    />
                </LocalizationProvider>
            </Box>

            {showClearButton && expirationDate && !disabled && (
                <IconButton
                    aria-label="Clear expiration date"
                    onClick={() => onExpirationDateChange(null)}
                    size="small"
                    sx={{
                        transform: 'translateY(-10px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <CancelIcon fontSize="small"/>
                </IconButton>
            )}
        </Stack>
    );
};

FormExpirationDatePicker.propTypes = {
    expirationDate: PropTypes.string,
    onExpirationDateChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    minDateTime: PropTypes.object,
    label: PropTypes.string,
    hideEmptyPrompt: PropTypes.bool,
    showClearButton: PropTypes.bool,
    sx: PropTypes.object,
};

export default FormExpirationDatePicker;
